// # Weak Spots Component for Step 2 Mentorship
import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../../auth/state';
import SharedHeader from '../../../components/SharedHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { 
  Paper, Typography, Box, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, TextField, Checkbox, Dialog, DialogTitle, DialogContent,
  DialogActions, DialogContentText, Select, MenuItem, FormControl,
  InputLabel, CircularProgress, Snackbar, Alert, IconButton,
  TextareaAutosize, Divider
} from '@mui/material';
import { 
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  BarChart as BarChartIcon, Timeline as TimelineIcon,
  ArrowUpward as ArrowUpwardIcon
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, LineChart, Line, ResponsiveContainer
} from 'recharts';
import './weakspots2.css';

// # Import Firebase
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';

// # Import library navigation button icon
import { FaBookMedical } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

// # Generate a unique ID for weak spots
const generateRandomId = () => {
  // # Format: weak_ISO-timestamp_random6chars
  const timestamp = new Date().toISOString();
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let randomStr = '';
  for (let i = 0; i < 6; i++) {
    randomStr += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return `weak_${timestamp}_${randomStr}`;
};

// # Chapter definitions with color coding for Step 2
const STEP2_CHAPTERS = [
  { name: 'Cardiovascular', color: '#FFE0E0' },
  { name: 'Respiratory & ENT', color: '#E0FFE0' },
  { name: 'Renal & Urology', color: '#E0E0FF' },
  { name: 'Endocrine', color: '#FFE0FF' },
  { name: 'Hematology & Oncology', color: '#E0FFFF' },
  { name: 'Immunology', color: '#FFFDE0' },
  { name: 'GIT', color: '#FFE0E8' },
  { name: 'Musculoskeletal', color: '#E0FFE8' },
  { name: 'Neurology', color: '#E0E8FF' },
  { name: 'Ophthalmology', color: '#FFE8E0' },
  { name: 'Psychiatry', color: '#E8FFE0' },
  { name: 'Dermatology', color: '#E8E0FF' },
  { name: 'Gynecology & Obstetrics', color: '#FFE8FF' },
  { name: 'Neonate & Infant', color: '#E8FFFF' },
  { name: 'Genetics', color: '#FFFFE8' },
  { name: 'Infectious Disease', color: '#F0E0E0' },
  { name: 'Surgery & Anesthesia', color: '#E0F0E0' },
  { name: 'Emergency, Trauma & Toxicology', color: '#E0E0F0' },
  { name: 'The Well Patient', color: '#F0E0F0' },
  { name: 'Biostatistics & Epidemiology', color: '#E0F0F0' },
  { name: 'Ethics & Communication', color: '#F0F0E0' },
  { name: 'Healthcare Delivery', color: '#F0E0F0' },
  { name: 'Quality & Safety', color: '#E0F0F0' },
  { name: 'Pharmacology Review', color: '#F0F0E0' }
];

function WeakSpots2({ isAdmin }) {
  const { user } = useAuthStore();
  const [weakSpotsMap, setWeakSpotsMap] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [editingSpot, setEditingSpot] = useState(null);
  const [deletingSpot, setDeletingSpot] = useState(null);
  const [viewingSpot, setViewingSpot] = useState(null);
  const [formData, setFormData] = useState({
    topic: '',
    chapter: '',
    details: '',
    chapterColor: '#E0E0FF',
    step: 'Step 2',
    practiced: false,
    mastered: false
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [showStats, setShowStats] = useState(false);
  // # Add state for target user ID and mentor status
  const [targetUserId, setTargetUserId] = useState(null);
  const [isMentor, setIsMentor] = useState(false);

  const navigate = useNavigate(); // # Add navigate hook
  const [activeTab, setActiveTab] = useState('table');
  const [loading, setLoading] = useState(true);
  const [weakSpots, setWeakSpots] = useState([]);
  
  // # Navigate to library function
  const navigateToLibrary = () => {
    navigate('/library/step2');
  };

  // # Get the correct user ID (mentee's ID when mentor is viewing)
  useEffect(() => {
    const menteeId = sessionStorage.getItem('currentViewingMenteeId');
    setTargetUserId(menteeId || user?.uid);
    setIsMentor(!!menteeId);
  }, [user?.uid]);

  // # Load weak spots data from Firestore
  useEffect(() => {
    const loadWeakSpots = async () => {
      if (!targetUserId) return;

      setIsLoading(true);
      try {
        // # Get data from s2_mentee collection
        const docRef = doc(db, 's2_mentee', targetUserId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          // # Get weakSpots map or default to empty object
          const data = docSnap.data();
          setWeakSpotsMap(data?.weakSpots || {});
        } else {
          // # If document doesn't exist, initialize with empty object
          setWeakSpotsMap({});
        }
      } catch (error) {
        console.error('Error loading weak spots:', error);
        setSnackbar({
          open: true,
          message: 'Error loading weak spots. Please try again.',
          severity: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadWeakSpots();
  }, [targetUserId]);

  // # Save weak spots to Firestore
  const saveWeakSpots = async (updatedSpotsMap) => {
    if (!targetUserId) return;

    try {
      const docRef = doc(db, 's2_mentee', targetUserId);
      const docSnap = await getDoc(docRef);
      const currentData = docSnap.exists() ? docSnap.data() : {};

      // # Update the weakSpots field while preserving other data
      await setDoc(docRef, {
        ...currentData,
        weakSpots: updatedSpotsMap
      }, { merge: true });
      
      return true;
    } catch (error) {
      console.error('Error saving weak spots:', error);
      return false;
    }
  };

  // # Handle add button click
  const handleAddClick = () => {
    setEditingSpot(null);
    setFormData({ 
      chapter: '', 
      topic: '', 
      details: '',
      chapterColor: '#E0E0FF', 
      step: 'Step 2', 
      practiced: false, 
      mastered: false 
    });
    setOpenDialog(true);
  };

  // # Handle edit button click
  const handleEditClick = (spot) => {
    setEditingSpot(spot);
    setFormData({
      chapter: spot.chapter,
      topic: spot.topic,
      details: spot.details || '',
      chapterColor: spot.chapterColor,
      step: spot.step,
      practiced: spot.practiced,
      mastered: spot.mastered
    });
    setOpenDialog(true);
  };

  // # Handle details button click
  const handleDetailsClick = (spot) => {
    setViewingSpot(spot);
    setFormData({
      ...formData,
      details: spot.details || ''
    });
    setOpenDetailsDialog(true);
  };

  // # Handle delete button click
  const handleDeleteClick = (spot) => {
    setDeletingSpot(spot);
    setOpenDeleteDialog(true);
  };

  // # Handle form submission
  const handleFormSubmit = async () => {
    if (!formData.chapter || !formData.topic) return;
    
    setIsSaving(true);
    const timestamp = new Date().toISOString();
    
    try {
      let updatedSpotsMap;
      
      if (editingSpot) {
        // # Update existing spot
        updatedSpotsMap = {
          ...weakSpotsMap,
          [editingSpot.id]: {
            ...editingSpot,
            chapter: formData.chapter,
            topic: formData.topic,
            details: formData.details || '',
            chapterColor: formData.chapterColor || STEP2_CHAPTERS.find(ch => ch.name === formData.chapter)?.color || '#E0E0FF',
            step: formData.step,
            practiced: formData.practiced,
            mastered: formData.mastered,
            updatedAt: timestamp
          }
        };
      } else {
        // # Add new spot
        const newId = generateRandomId();
        const newSpot = {
          id: newId,
          chapter: formData.chapter,
          topic: formData.topic,
          details: formData.details || '',
          chapterColor: formData.chapterColor || STEP2_CHAPTERS.find(ch => ch.name === formData.chapter)?.color || '#E0E0FF',
          step: formData.step || 'Step 2',
          reviewCount: 0,
          practiced: formData.practiced,
          mastered: formData.mastered,
          createdAt: timestamp,
          updatedAt: timestamp
        };
        
        updatedSpotsMap = {
          ...weakSpotsMap,
          [newId]: newSpot
        };
      }
      
      // # Save to Firestore
      const success = await saveWeakSpots(updatedSpotsMap);
      
      if (success) {
        // # Update local state
        setWeakSpotsMap(updatedSpotsMap);
        
        // # Close dialog and show success message
        setOpenDialog(false);
        setFormData({ chapter: '', topic: '', details: '', chapterColor: '#E0E0FF', step: 'Step 2', practiced: false, mastered: false });
        setEditingSpot(null);
        setSnackbar({
          open: true,
          message: editingSpot ? 'Weak spot updated successfully!' : 'New weak spot added successfully!',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Error saving weak spot. Please try again.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error in form submission:', error);
      setSnackbar({
        open: true,
        message: 'Error saving weak spot. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // # Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deletingSpot) return;
    
    setIsSaving(true);
    try {
      // # Filter out the spot to delete
      const updatedSpotsMap = { ...weakSpotsMap };
      delete updatedSpotsMap[deletingSpot.id];
      
      // # Save to Firestore
      const success = await saveWeakSpots(updatedSpotsMap);
      
      if (success) {
        // # Update local state
        setWeakSpotsMap(updatedSpotsMap);
        
        // # Close dialog and show success message
        setOpenDeleteDialog(false);
        setDeletingSpot(null);
        setSnackbar({
          open: true,
          message: 'Weak spot deleted successfully!',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Error deleting weak spot. Please try again.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error deleting weak spot:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting weak spot. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // # Update a specific field of a weak spot
  const updateWeakSpot = async (id, field, value) => {
    if (!id || !field) return;
    
    setIsSaving(true);
    const timestamp = new Date().toISOString();
    
    try {
      // # Update local state first
      const updatedSpotsMap = { ...weakSpotsMap };
      const updatedSpot = { ...updatedSpotsMap[id] };
      
      if (field === 'reviewCount') {
        updatedSpot.reviewCount = updatedSpot.reviewCount + 1;
      } else {
        updatedSpot[field] = value;
      }
      updatedSpot.updatedAt = timestamp;
      updatedSpotsMap[id] = updatedSpot;
      
      // # Save to Firestore
      const success = await saveWeakSpots(updatedSpotsMap);
      
      if (success) {
        // # Update local state
        setWeakSpotsMap(updatedSpotsMap);
        
        // # Show success message
        setSnackbar({
          open: true,
          message: `Weak spot ${field === 'reviewCount' ? 'review count increased' : 'updated'} successfully!`,
          severity: 'success'
        });
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      console.error('Error updating weak spot:', error);
      
      // # Revert local state on error
      setWeakSpotsMap(prev => {
        const updatedSpotsMap = { ...prev };
        const updatedSpot = { ...updatedSpotsMap[id] };
        
        if (field === 'reviewCount') {
          updatedSpot.reviewCount = updatedSpot.reviewCount - 1;
        } else {
          updatedSpot[field] = !value;
        }
        updatedSpot.updatedAt = timestamp;
        updatedSpotsMap[id] = updatedSpot;
        return updatedSpotsMap;
      });
      
      // # Show error message
      setSnackbar({
        open: true,
        message: 'Error updating weak spot. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // # Handle details update
  const handleDetailsUpdate = async () => {
    if (!viewingSpot) return;
    
    setIsSaving(true);
    const timestamp = new Date().toISOString();
    
    try {
      // # Update local state first
      const updatedSpotsMap = { ...weakSpotsMap };
      const updatedSpot = { ...updatedSpotsMap[viewingSpot.id] };
      
      updatedSpot.details = formData.details;
      updatedSpot.updatedAt = timestamp;
      updatedSpotsMap[viewingSpot.id] = updatedSpot;
      
      // # Save to Firestore
      const success = await saveWeakSpots(updatedSpotsMap);
      
      if (success) {
        // # Update local state
        setWeakSpotsMap(updatedSpotsMap);
        
        // # Close dialog and show success message
        setOpenDetailsDialog(false);
        setViewingSpot(null);
        setSnackbar({
          open: true,
          message: 'Details updated successfully!',
          severity: 'success'
        });
      } else {
        throw new Error('Failed to update details');
      }
    } catch (error) {
      console.error('Error updating details:', error);
      setSnackbar({
        open: true,
        message: 'Error updating details. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // # Calculate statistics from weakSpotsMap
  const calculateStats = () => {
    const spots = Object.values(weakSpotsMap);
    const totalSpots = spots.length;
    const practicedSpots = spots.filter(spot => spot.practiced).length;
    const masteredSpots = spots.filter(spot => spot.mastered).length;
    
    // # Calculate chapter distribution
    const chapterStats = spots.reduce((acc, spot) => {
      acc[spot.chapter] = (acc[spot.chapter] || 0) + 1;
      return acc;
    }, {});

    // # Calculate review trends (last 7 days)
    const reviewTrends = Array(7).fill(0).map((_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const count = spots.filter(spot => 
        spot.updatedAt && spot.updatedAt.startsWith(dateStr)
      ).length;
      return { date: dateStr, reviews: count };
    }).reverse();

    return {
      totalSpots,
      practicedSpots,
      masteredSpots,
      progressPercentage: totalSpots ? Math.round((masteredSpots / totalSpots) * 100) : 0,
      chapterStats,
      reviewTrends
    };
  };

  // # Scroll handlers
  const scrollToStats = () => {
    document.querySelector('.weakspots2_stats_section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const stats = calculateStats();

  // # Chart colors
  const CHART_COLORS = ['#4caf50', '#2196f3', '#ff9800', '#e91e63', '#9c27b0'];

  return (
    <div className="weakspots2_container">
      <SharedHeader />
      {/* # Library Navigation Button */}
      <div className="weakspots2_library_nav_button" onClick={navigateToLibrary} title="Go to Step 2 Library">
        <FaBookMedical />
        <span>Library</span>
      </div>
      <div className="weakspots2_main">
        <div className="weakspots2_content">
          <div className="weakspots2_header_container">
            <div className="weakspots2_left_section">
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddClick}
                className="weakspots2_add_button"
                disabled={isSaving}
              >
                Add Weak Spot
              </Button>
            </div>
            <div className="weakspots2_title_section">
              <Typography variant="h4" component="h1" className="weakspots2_title">
                Step 2 Weak Spots Tracker {isMentor && <span className="weakspots2_mentor_pill">Mentor</span>}
              </Typography>
            </div>
            <div className="weakspots2_buttons_section">
              <Button
                variant="contained"
                className="weakspots2_stats_nav_button"
                onClick={scrollToStats}
                startIcon={<BarChartIcon />}
              >
                Go to Stats Dashboard
              </Button>
            </div>
          </div>

          {/* # Loading indicator */}
          {isLoading ? (
            <Box className="weakspots2_loading">
              <CircularProgress />
              <Typography variant="body1">Loading weak spots...</Typography>
            </Box>
          ) : (
            /* # Weak spots table */
            <TableContainer className="weakspots2_table_container">
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Topic</TableCell>
                    <TableCell>Chapter</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Last Review</TableCell>
                    <TableCell>Practiced</TableCell>
                    <TableCell>Mastered</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.keys(weakSpotsMap).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body1" className="weakspots2_empty_message">
                          No weak spots added yet. Click "Add Weak Spot" to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    Object.entries(weakSpotsMap).map(([id, spot]) => (
                      <TableRow key={id} style={{ backgroundColor: spot.chapterColor + '40' }}>
                        <TableCell>{spot.topic}</TableCell>
                        <TableCell>{spot.chapter}</TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            className="weakspots2_details_button"
                            onClick={() => handleDetailsClick(spot)}
                          >
                            View Details
                          </Button>
                        </TableCell>
                        <TableCell>{new Date(spot.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Box className="weakspots2_review_cell">
                            <span>
                              {spot.lastReview
                                ? new Date(spot.lastReview).toLocaleDateString()
                                : 'Never'}
                            </span>
                            <Button
                              size="small"
                              variant="outlined"
                              className="weakspots2_review_button"
                              onClick={() => updateWeakSpot(id, 'lastReview', new Date().toISOString())}
                            >
                              Review Now
                            </Button>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            checked={spot.practiced}
                            onChange={(e) => updateWeakSpot(id, 'practiced', e.target.checked)}
                            className="weakspots2_checkbox"
                          />
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            checked={spot.mastered}
                            onChange={(e) => updateWeakSpot(id, 'mastered', e.target.checked)}
                            className="weakspots2_checkbox"
                          />
                        </TableCell>
                        <TableCell>
                          <Box className="weakspots2_actions">
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(spot)}
                              className="weakspots2_edit_button"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(spot)}
                              className="weakspots2_delete_button"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* # Stats Dashboard Section */}
          <section className="weakspots2_stats_section">
            <div className="weakspots2_stats_header">
              <Typography className="weakspots2_stats_title">
                Step 2 Weak Spots Statistics
              </Typography>
              <Button
                variant="contained"
                className="weakspots2_stats_nav_button"
                onClick={scrollToTop}
                startIcon={<ArrowUpwardIcon />}
              >
                Back to Table
              </Button>
            </div>

            {/* # Stats Cards Grid */}
            <div className="weakspots2_stats_grid">
              <div className="weakspots2_stats_card">
                <div className="weakspots2_stats_card_title">Total Weak Spots</div>
                <div className="weakspots2_stats_card_value">{stats.totalSpots}</div>
                <div className="weakspots2_stats_card_subtitle">Tracked topics</div>
              </div>

              <div className="weakspots2_stats_card">
                <div className="weakspots2_stats_card_title">Practiced</div>
                <div className="weakspots2_stats_card_value">{stats.practicedSpots}</div>
                <div className="weakspots2_stats_card_subtitle">Topics reviewed</div>
              </div>

              <div className="weakspots2_stats_card">
                <div className="weakspots2_stats_card_title">Mastered</div>
                <div className="weakspots2_stats_card_value">{stats.masteredSpots}</div>
                <div className="weakspots2_stats_card_subtitle">Topics mastered</div>
              </div>

              <div className="weakspots2_stats_card">
                <div className="weakspots2_stats_card_title">Progress</div>
                <div className="weakspots2_stats_card_value">{stats.progressPercentage}%</div>
                <div className="weakspots2_stats_card_subtitle">Overall completion</div>
              </div>
            </div>

            {/* # Charts Container */}
            <div className="weakspots2_charts_container">
              {/* # First Row - Full Width */}
              <div className="weakspots2_charts_row weakspots2_charts_row_full">
                <div className="weakspots2_chart_wrapper">
                  <h3 className="weakspots2_chart_title">Review Activity (Last 7 Days)</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={stats.reviewTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => new Date(date).toLocaleDateString()}
                      />
                      <YAxis />
                      <Tooltip 
                        labelFormatter={(date) => new Date(date).toLocaleDateString()}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="reviews" 
                        stroke="#2196f3" 
                        strokeWidth={2}
                        dot={{ fill: '#2196f3' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* # Second Row - Split */}
              <div className="weakspots2_charts_row weakspots2_charts_row_split">
                {/* # Chapter Distribution Chart */}
                <div className="weakspots2_chart_wrapper">
                  <h3 className="weakspots2_chart_title">Topics by Chapter</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={Object.entries(stats.chapterStats).map(([name, value], index) => ({
                          name,
                          value
                        }))}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {Object.entries(stats.chapterStats).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* # Progress Distribution Chart */}
                <div className="weakspots2_chart_wrapper">
                  <h3 className="weakspots2_chart_title">Progress Distribution</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={[
                      { name: 'Not Started', value: stats.totalSpots - stats.practicedSpots },
                      { name: 'Practiced', value: stats.practicedSpots - stats.masteredSpots },
                      { name: 'Mastered', value: stats.masteredSpots }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="value" fill="#4caf50" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* # Add/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => !isSaving && setOpenDialog(false)}
        className="weakspots2_dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingSpot ? 'Edit Weak Spot' : 'Add New Weak Spot'}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense" className="weakspots2_dialog_input">
            <InputLabel id="chapter-label">Chapter</InputLabel>
            <Select
              labelId="chapter-label"
              value={formData.chapter}
              onChange={(e) => {
                const selectedChapter = STEP2_CHAPTERS.find(
                  (ch) => ch.name === e.target.value
                );
                setFormData({
                  ...formData,
                  chapter: e.target.value,
                  chapterColor: selectedChapter ? selectedChapter.color : '#E0E0E0'
                });
              }}
              label="Chapter"
              disabled={isSaving}
            >
              {STEP2_CHAPTERS.map((chapter) => (
                <MenuItem 
                  key={chapter.name} 
                  value={chapter.name}
                  style={{ backgroundColor: chapter.color + '40' }}
                >
                  {chapter.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            margin="dense"
            label="Topic"
            type="text"
            fullWidth
            value={formData.topic}
            onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
            className="weakspots2_dialog_input"
            disabled={isSaving}
          />
          
          <TextField
            margin="dense"
            label="Details"
            multiline
            rows={4}
            fullWidth
            value={formData.details}
            onChange={(e) => setFormData({ ...formData, details: e.target.value })}
            className="weakspots2_dialog_input"
            disabled={isSaving}
            placeholder="Enter detailed notes about this weak spot..."
          />
          
          <Box sx={{ display: 'flex', mt: 2, gap: 2 }}>
            <FormControl className="weakspots2_dialog_input">
              <InputLabel id="practiced-label">Practiced</InputLabel>
              <Select
                labelId="practiced-label"
                value={formData.practiced}
                onChange={(e) => setFormData({ ...formData, practiced: e.target.value })}
                label="Practiced"
                disabled={isSaving}
              >
                <MenuItem value={true}>Yes</MenuItem>
                <MenuItem value={false}>No</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl className="weakspots2_dialog_input">
              <InputLabel id="mastered-label">Mastered</InputLabel>
              <Select
                labelId="mastered-label"
                value={formData.mastered}
                onChange={(e) => setFormData({ ...formData, mastered: e.target.value })}
                label="Mastered"
                disabled={isSaving}
              >
                <MenuItem value={true}>Yes</MenuItem>
                <MenuItem value={false}>No</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenDialog(false)} 
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleFormSubmit} 
            variant="contained" 
            color="primary"
            disabled={!formData.chapter || !formData.topic || isSaving}
            startIcon={isSaving ? <CircularProgress size={20} /> : null}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* # Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => !isSaving && setOpenDeleteDialog(false)}
        className="weakspots2_delete_dialog"
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the weak spot "{deletingSpot?.topic}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenDeleteDialog(false)} 
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} /> : null}
          >
            {isSaving ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* # Details Dialog */}
      <Dialog
        open={openDetailsDialog}
        onClose={() => !isSaving && setOpenDetailsDialog(false)}
        className="weakspots2_details_dialog"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="weakspots2_details_dialog_title">
          <Typography variant="h6" component="div">
            {viewingSpot && viewingSpot.chapter}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            {viewingSpot && viewingSpot.topic}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom color="primary" fontWeight={600}>
              Details:
            </Typography>
            <TextareaAutosize
              minRows={3}
              maxRows={15}
              value={formData.details}
              onChange={(e) => setFormData({ ...formData, details: e.target.value })}
              className="weakspots2_details_textarea"
              disabled={isSaving}
              placeholder="Enter detailed notes about this weak spot..."
            />
          </Box>
          
          {viewingSpot && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box className="weakspots2_details_info">
                <Box className="weakspots2_details_info_item">
                  <Typography variant="body2" color="textSecondary">
                    Created
                  </Typography>
                  <Typography variant="body1">
                    {new Date(viewingSpot.createdAt).toLocaleString()}
                  </Typography>
                </Box>
                <Box className="weakspots2_details_info_item">
                  <Typography variant="body2" color="textSecondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body1">
                    {new Date(viewingSpot.updatedAt).toLocaleString()}
                  </Typography>
                </Box>
                <Box className="weakspots2_details_info_item">
                  <Typography variant="body2" color="textSecondary">
                    Review Count
                  </Typography>
                  <Typography variant="body1" color="primary" fontWeight={600}>
                    {viewingSpot.reviewCount || 0}
                  </Typography>
                </Box>
                <Box className="weakspots2_details_info_item">
                  <Typography variant="body2" color="textSecondary">
                    Status
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Checkbox
                      checked={viewingSpot.practiced}
                      disabled
                      size="small"
                    />
                    <Typography variant="body2">Practiced</Typography>
                    <Checkbox
                      checked={viewingSpot.mastered}
                      disabled
                      size="small"
                    />
                    <Typography variant="body2">Mastered</Typography>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <Divider />
        <DialogActions className="weakspots2_details_dialog_actions">
          <Button 
            onClick={() => setOpenDetailsDialog(false)} 
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDetailsUpdate} 
            variant="contained" 
            color="primary"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} /> : null}
          >
            {isSaving ? 'Saving...' : 'Update Details'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* # Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <DashboardFooter />
    </div>
  );
}

export default WeakSpots2;
